/** The create-export form's values and their conversion to the wire request (U096-U098). */
import {
  FIDELITIES,
  JOB_KINDS,
  type ExportRequest,
  type Fidelity,
  type JobKind,
} from "../../contracts/delivery.ts";
import { MODES, type Mode } from "../../contracts/envelope.ts";

export const REVIEW_PERIODS = ["DAILY", "WEEKLY", "MONTHLY"] as const;
/** An expiring export (U097) lives one hour. */
export const EXPIRING_EXPORT_SECONDS = 3600;

export interface ExportForm {
  readonly kind: string;
  readonly workspace: string;
  readonly shared: string;
  readonly mode: string;
  readonly fidelity: string;
  readonly period: string;
}

const ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

function member<const T extends readonly string[]>(list: T, value: string): T[number] | null {
  return list.find((candidate) => candidate === value) ?? null;
}

/** Why the form cannot be submitted, or `null` when it can. */
export function formProblem(form: ExportForm): string | null {
  if (member(JOB_KINDS, form.kind) === null) {
    return "Choose what to create.";
  }
  if (!ID.test(form.workspace)) {
    return "Enter the workspace id.";
  }
  if (form.shared !== "" && !ID.test(form.shared)) {
    return "The shared workspace id is not valid.";
  }
  if (member(MODES, form.mode) === null) {
    return "Choose the mode of the data.";
  }
  if (member(FIDELITIES, form.fidelity) === null) {
    return "Choose the fidelity of the data.";
  }
  if (form.kind === "PERIODIC_REVIEW" && member(REVIEW_PERIODS, form.period) === null) {
    return "Choose the review period.";
  }
  return null;
}

/** Build the request of a valid form; `null` when the form has a problem. */
export function buildExportRequest(
  form: ExportForm,
  accountId: string | null,
  clientKey: string,
): ExportRequest | null {
  const kind: JobKind | null = member(JOB_KINDS, form.kind);
  const mode: Mode | null = member(MODES, form.mode);
  const fidelity: Fidelity | null = member(FIDELITIES, form.fidelity);
  if (formProblem(form) !== null || kind === null || mode === null || fidelity === null) {
    return null;
  }
  return {
    clientKey,
    kind,
    workspaceId: form.workspace,
    accountId,
    sharedWorkspace: form.shared === "" ? null : form.shared,
    mode,
    fidelity,
    query: kind === "PERIODIC_REVIEW" ? [["period", form.period]] : [],
    ttlSeconds: kind === "EXPIRING_EXPORT" ? EXPIRING_EXPORT_SECONDS : null,
  };
}
